"use client"

import * as React from "react"
import { Check, ChevronDown, Gamepad2, Trophy } from "lucide-react"
import { Button } from "@/components/ui/button"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"
import { cn } from "@/lib/utils"

const INDIVIDUAL_GAMES = [
  { id: "valorant", name: "Valorant" },
  { id: "rocket-league", name: "Rocket League" },
  { id: "cod-warzone", name: "Call of Duty: Warzone" },
  { id: "apex", name: "Apex Legends" },
  { id: "fortnite", name: "Fortnite" },
  { id: "league", name: "League of Legends" },
  { id: "cs2", name: "Counter-Strike 2" },
  { id: "overwatch", name: "Overwatch 2" },
  { id: "halo", name: "Halo Infinite" },
  { id: "fifa", name: "EA FC 25" },
  { id: "madden", name: "Madden 25" },
  { id: "nba2k", name: "NBA 2K25" },
]

const MULTI_GAME_LEAGUES = [
  { id: "sports-night", name: "Sports Night", description: "FIFA, Madden, NBA 2K rotation" },
  { id: "fps-sundays", name: "FPS Sundays", description: "Rotating shooters weekly" },
  { id: "battle-royale-bash", name: "Battle Royale Bash", description: "Warzone, Apex, Fortnite" },
  { id: "retro-nights", name: "Retro Game Nights", description: "Classic games & remasters" },
]

interface GameSelectorProps {
  selectedGames: string[]
  onSelectionChange: (games: string[]) => void
}

export function GameSelector({ selectedGames, onSelectionChange }: GameSelectorProps) {
  const [open, setOpen] = React.useState(false)

  const allSelected = selectedGames.length === 0 || selectedGames.includes("all")

  const toggleGame = (gameId: string) => {
    if (gameId === "all") {
      onSelectionChange([])
      return
    }

    const newSelection = selectedGames.filter(id => id !== "all")
    if (newSelection.includes(gameId)) {
      onSelectionChange(newSelection.filter(id => id !== gameId))
    } else {
      onSelectionChange([...newSelection, gameId])
    }
  }

  const getDisplayText = () => {
    if (allSelected || selectedGames.length === 0) return "All Games"
    if (selectedGames.length === 1) {
      const game = [...INDIVIDUAL_GAMES, ...MULTI_GAME_LEAGUES].find(g => g.id === selectedGames[0])
      return game?.name || "1 selected"
    }
    return `${selectedGames.length} selected`
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="w-full justify-between bg-background/80 backdrop-blur-sm border-border hover:border-primary/50 hover:bg-background h-12 text-left"
        >
          <span className="flex items-center gap-2 truncate">
            <Gamepad2 className="h-4 w-4 text-primary shrink-0" />
            <span className="truncate">{getDisplayText()}</span>
          </span>
          <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-72 p-0 bg-card border-border" align="start">
        <div className="max-h-80 overflow-y-auto">
          {/* All Games Option */}
          <div
            className={cn(
              "flex items-center gap-3 px-3 py-2.5 cursor-pointer hover:bg-secondary/50 border-b border-border",
              allSelected && "bg-primary/10"
            )}
            onClick={() => toggleGame("all")}
          >
            <div className={cn(
              "h-4 w-4 rounded border flex items-center justify-center shrink-0",
              allSelected ? "bg-primary border-primary" : "border-muted-foreground"
            )}>
              {allSelected && <Check className="h-3 w-3 text-primary-foreground" />}
            </div>
            <span className="font-medium">All Games</span>
          </div>

          {/* Multi-Game Leagues Section */}
          <div className="px-3 py-2 bg-secondary/30">
            <div className="flex items-center gap-2 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
              <Trophy className="h-3 w-3" />
              Multi-Game Leagues
            </div>
          </div>
          {MULTI_GAME_LEAGUES.map((league) => {
            const isSelected = selectedGames.includes(league.id)
            return (
              <div
                key={league.id}
                className={cn(
                  "flex items-center gap-3 px-3 py-2.5 cursor-pointer hover:bg-secondary/50",
                  isSelected && "bg-primary/10"
                )}
                onClick={() => toggleGame(league.id)}
              >
                <div className={cn(
                  "h-4 w-4 rounded border flex items-center justify-center shrink-0",
                  isSelected ? "bg-primary border-primary" : "border-muted-foreground"
                )}>
                  {isSelected && <Check className="h-3 w-3 text-primary-foreground" />}
                </div>
                <div className="min-w-0">
                  <div className="font-medium truncate">{league.name}</div>
                  <div className="text-xs text-muted-foreground truncate">{league.description}</div>
                </div>
              </div>
            )
          })}

          {/* Individual Games Section */}
          <div className="px-3 py-2 bg-secondary/30">
            <div className="flex items-center gap-2 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
              <Gamepad2 className="h-3 w-3" />
              Individual Games
            </div>
          </div>
          {INDIVIDUAL_GAMES.map((game) => {
            const isSelected = selectedGames.includes(game.id)
            return (
              <div
                key={game.id}
                className={cn(
                  "flex items-center gap-3 px-3 py-2.5 cursor-pointer hover:bg-secondary/50",
                  isSelected && "bg-primary/10"
                )}
                onClick={() => toggleGame(game.id)}
              >
                <div className={cn(
                  "h-4 w-4 rounded border flex items-center justify-center shrink-0",
                  isSelected ? "bg-primary border-primary" : "border-muted-foreground"
                )}>
                  {isSelected && <Check className="h-3 w-3 text-primary-foreground" />}
                </div>
                <span className="truncate">{game.name}</span>
              </div>
            )
          })}
        </div>
      </PopoverContent>
    </Popover>
  )
}
